import React, { useEffect, useState } from 'react';
import { Button } from "react-bootstrap";
import { useSelector } from 'react-redux'
import _ from "lodash";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import ItemCard from "./ItemCard";
import AddItemCard from './AddItem';
import HorizontalItemCard from "./HorizontalItemCard";
import ReactPaginate from 'react-paginate';
import { OrderType } from "../../constants/Types";
import { StateMachineManager } from '../../utils/stateMachine';
import ActionConfirmForm from '../common/ActionConfirmForm';
import { PROMPT_ACTION_FORM } from '../../constants/Messages';
import "./ItemCard.scss";
import { USER_SETTINGS } from '../../constants/UserSettings';
import { faSearchPlus } from '@fortawesome/free-solid-svg-icons';

const COUNT_PER_PAGE = 15;

const ReactSwal = withReactContent(Swal);

const ItemCards = props => {
  const exSearchKey = useSelector(({ filters }) => filters.searchKey)

  const [data, setData] = useState([]);
  const [searchKey, setSearchKey] = useState(exSearchKey);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);

  let pageCount = 0;

  useEffect(() => {
    const searchString = props.history.location.search.replace('?', '');
    const pairStrings = searchString.split('&');
    let curPageNum = 0;

    pairStrings.forEach(pair => {
      if (pair.includes('page=')) {
        const pageNum = pair.replace('page=', '');
        curPageNum = parseInt(pageNum) - 1;
      }
    })

    setCurrentPage(curPageNum);

    StateMachineManager.Instance.setChangeStateHandler(current => {
      setCurrentPage(0);
    });
  }, []);

  useEffect(() => {
    setSelectedItems([]);
  }, [props.currentStatus]);

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
    props.history.push(`/?page=${data.selected + 1}`);
  };

  const handleDidUpdateSearchKey = (incomingSearchKey) => {
    const didSearchKeyUpdate = searchKey !== incomingSearchKey;
    if (didSearchKeyUpdate) {
      // reset current page or new results
      setCurrentPage(0);
      setSearchKey(incomingSearchKey);
    }
  }

  const sorter = (a, b) => {
    switch (props.filters.orderType) {
      case OrderType.DATE_ASCENDING: {
        if (a.createdAt > b.createdAt) {
          return 1;
        } else if (a.createdAt === b.createdAt) {
          return 0;
        } else {
          return -1;
        }
      }
      case OrderType.DATE_DESCENDING: {
        if (a.createdAt < b.createdAt) {
          return 1;
        } else if (a.createdAt === b.createdAt) {
          return 0;
        } else {
          return -1;
        }
      }
      case OrderType.DOCUMENT_TYPE: {
        const typeA = a?.meta?.contentType?.toLowerCase?.() || '';
        const typeB = b?.meta?.contentType?.toLowerCase?.() || '';

        if (typeA === '') {
          return 1;
        }

        if (typeA > typeB) {
          return 1;
        } else if (typeA === typeB) {
          const titleA = a?.meta?.contentTitle?.toLowerCase?.() || '';
          const titleB = b?.meta?.contentTitle?.toLowerCase?.() || '';

          if (titleA > titleB) {
            return 1;
          } else if (titleA === titleB) {
            return 0;
          } else {
            return -1;
          }
        } else {
          return -1;
        }
      }
      case OrderType.FILE_NAME: {
        const titleA = (a?.originalName || a?.meta?.contentTitle || '').toLowerCase();
        const titleB = (b?.originalName || b?.meta?.contentTitle || '').toLowerCase();

        if (titleA > titleB) {
          return 1;
        } else if (titleA === titleB) {
          return 0;
        } else {
          return -1;
        } 
      }
      case OrderType.FILE_TYPE: {
        const propertyName = OrderType.FILE_NAME === props.filters.orderType ? 'name' : 'ext';
        const typeA = (a?.[propertyName] || '').toLowerCase();
        const typeB = (b?.[propertyName] || '').toLowerCase();

        if (typeA === '') {
          return -1;
        }

        if (typeA > typeB) {
          return 1;
        } else if (typeA === typeB) {
          const titleA = a?.meta?.contentTitle?.toLowerCase?.() || '';
          const titleB = b?.meta?.contentTitle?.toLowerCase?.() || '';

          if (titleA > titleB) {
            return 1;
          } else if (titleA === titleB) {
            return 0;
          } else {
            return -1;
          }
        } else {
          return -1;
        }
      }
      default:
        return 1;
    }
  }

  const changeNotificationPermission = () => {
    if (_.get(props.userInfo, USER_SETTINGS.APP_OPTIONS.PUSH_ENABLED, true)) {
      if (!('Notification' in window)) {
        console.log("This browser does not support notifications.");
      } else {
        if (Notification.permission !== 'granted' && Notification.permission !== 'denied') {

          ReactSwal.fire({
            allowOutsideClick: true,
            allowEscapeKey: true,
            allowEnterKey: false,
            showCancelButton: false,
            showConfirmButton: false,
            html: (
              <ActionConfirmForm
                onConfirm={ReactSwal.clickConfirm}
                onClose={ReactSwal.clickCancel}
                title={PROMPT_ACTION_FORM.NOTIFICATIONS.TITLE}
                question={PROMPT_ACTION_FORM.NOTIFICATIONS.BODY}
              />
            )
          })
          .then(async result => {
            if (result.value) {
              try {
                await Notification.requestPermission();
              } catch (error) {
                console.log('error happened during request permission for browser notification');
                console.log(error);
              }
            }
          });
        }
      }
    }
  }

  const filterItemsHandler = () => {
    handleDidUpdateSearchKey(exSearchKey);
    const { filters } = props;

    const items = props.items ? props.items : [];

    const filteredItems = items.filter(item => {
      if (!item) return false;
      
      const isFavorite = _.get(props, 'settings.userInfo.collectionFavorites', []).includes(item.id);
      const isUpvoted = _.get(item, 'meta.upvoteCount', 0) > 0;
      const otherFlag = ((filters.isVoted && isUpvoted) || (filters.isFavorite && isFavorite) || (filters.isPopular && true) || (!filters.isVoted && !filters.isFavorite && !filters.isPopular));

      return otherFlag;
    }).sort(sorter);

    return filteredItems;
  }

  const onClickItemCard = (itemId) => {
    if (props.currentStatus === 'collectionEditing') {
      return;
    }

    let newSelectedItems = [...selectedItems];

    if (newSelectedItems.includes(itemId)) {
      newSelectedItems = newSelectedItems.filter(it => it !== itemId);
    } else {
      newSelectedItems.push(itemId);
    }

    setSelectedItems(newSelectedItems);
  }

  const onClickCardsContainer = (e) => {
    setSelectedItems([]);
  }

  const filteredItems = filterItemsHandler();
  
  pageCount = Math.ceil(filteredItems.length / COUNT_PER_PAGE);
  const currentSelectedPage = currentPage >= pageCount ? 0 : currentPage;
  const dataPerPage = filteredItems.slice(COUNT_PER_PAGE * currentSelectedPage, Math.min(COUNT_PER_PAGE * (currentSelectedPage + 1), filteredItems.length));
  
  return (
    <div className={`w-100 item-cards-container${props.currentStatus === 'collectionEditing' ? ' collection-editing' : ''} ${props.className}`} 
      draggable="false"
      onClick={onClickCardsContainer}>

      {selectedItems.length > 0 && (
        <div className="selected-count-box">
          <span>{selectedItems.length} items selected</span>
          <Button
            variant="outline-danger"
            className="btn btn-outline-danger"
            size="sm"
            onClick={onClickCardsContainer}
          >
            Clear selection
          </Button>
        </div>
      )}
      {props.horizontal ?
        <div className={`item-cards horizontal${props.currentStatus === 'collectionEditing' ? ' collection-editing' : ''}`}
        draggable="false">
          {
            dataPerPage.map(item => 
              <HorizontalItemCard
                data={item}
                key={item.id}
                history={props.history}
                draggable
                changeNotificationPermission={changeNotificationPermission}
                onTagSelect={props.onTagSelect}
                deleteItem={props.deleteItem}
                // onClick={props.isAdmin ? onClickItemCard : ''} // disabled for now
                isSelected={selectedItems.includes(item.id)}
                selectedItems={selectedItems}
              />
            )
          }
          {props.isAdmin && !props.isCollection && <AddItemCard horizontal onClick={props.openUpload} />}
        </div>
        : 
        <div className={`item-cards vertical${props.currentStatus === 'collectionEditing' ? ' collection-editing' : ''}`}
        draggable="false">
          {
            dataPerPage.map(item => 
              <ItemCard
                data={item}
                key={item.id}
                history={props.history}
                draggable
                changeNotificationPermission={changeNotificationPermission}
                onTagSelect={props.onTagSelect}
                deleteItem={props.deleteItem}
                // onClick={props.isAdmin ? onClickItemCard : ''} // disabled for now
                isSelected={selectedItems.includes(item.id)}
                selectedItems={selectedItems}
              />
            )
          }
          {props.isAdmin && !props.isCollection && <AddItemCard onClick={props.openUpload} />}
        </div>
      }
      {
        pageCount > 1 &&
          <div className="pagination-container" onClick={e => e.stopPropagation()}>
            <ReactPaginate
              previousLabel={'<'}
              nextLabel={'>'}
              breakLabel={'...'}
              breakClassName={'break-me'}
              pageCount={pageCount}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageClick}
              containerClassName={'pagination'}
              subContainerClassName={'pages pagination'}
              activeClassName={'active'}
              forcePage={currentSelectedPage}
            />
          </div>
      }
    </div>
  );
}

export default ItemCards;
